/* eslint-disable class-methods-use-this */
import { Language } from '@nx-smartmonkey/shared/domain';
import { Cookies, Requests } from '@nx-smartmonkey/shared/helpers';
import { CookiesConstants } from '../../constants/Cookies';
import { TwoFactorMethod } from '../../pages/auth/login/context/reducer';
import { SessionRepository } from './SessionRepository';

const DAYS_TO_EXPIRE = 14;

const DEFAULT_COUNTRY_CODE = `ES`;

export class SessionRepositoryApi implements SessionRepository {
  constructor(private requests: Requests, private cookies: Cookies) {}

  private countryCode: string | undefined;

  private language: Language | undefined;

  hasCookie(cookieName: string): boolean {
    return this.cookies.hasCookie(cookieName);
  }

  getCookie(cookieName: string): string | undefined {
    return this.cookies.getCookie(cookieName);
  }

  isLogged(): boolean {
    return this.cookies.hasCookie(CookiesConstants.SM_PLANNER_COOKIE_NAME);
  }

  getToken(): string | undefined {
    return this.cookies.getCookie(CookiesConstants.SM_PLANNER_COOKIE_NAME);
  }

  setToken({ token }: { token: string }) {
    return this.cookies.setCookie(CookiesConstants.SM_PLANNER_COOKIE_NAME, token, DAYS_TO_EXPIRE);
  }

  getAsUser(): string | undefined {
    const asVehicle = localStorage.getItem(`AS_USER`);
    return asVehicle ?? undefined;
  }

  saveAsUser(id: string) {
    this.requests.setAsUserId(id);
    localStorage.setItem(`AS_USER`, id);
  }

  deleteAsUser() {
    this.requests.setAsUserId(undefined);
    localStorage.removeItem(`AS_USER`);
  }

  setLanguage({ language }: { language: Language }) {
    this.language = language;
  }

  getLanguage(): Language | undefined {
    return this.language;
  }

  getAuthenticationHeader(): Record<string, any> {
    const token = this.cookies.getCookie(CookiesConstants.SM_PLANNER_COOKIE_NAME);
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  async createSession({
    email,
    password,
  }: {
    email: string;
    password: string;
  }): Promise<{ twoFactorMethods: TwoFactorMethod[]; phone?: string }> {
    this.deleteAsUser();
    const response = await this.requests.get({ uri: `/login`, params: { email, password, keep_session: true } });
    const { token, phone, twoFactor_methods: twoFactorMethods } = response.data;

    if (twoFactorMethods.length > 0) {
      return { twoFactorMethods, phone };
    }

    this.cookies.setCookie(CookiesConstants.SM_PLANNER_COOKIE_NAME, token, DAYS_TO_EXPIRE);

    return { twoFactorMethods: [], phone: undefined };
  }

  async validateSession({ otpCode }: { otpCode: string }): Promise<string> {
    if (!this.cookies.hasCookie(CookiesConstants.R_PLANNER_COOKIE_2FA)) {
      throw new Error(`Could not validate: invalid session token`);
    }

    const queryToken = this.cookies.getCookie(CookiesConstants.R_PLANNER_COOKIE_2FA);
    const response = await this.requests.get({ uri: `/validate`, params: { token: queryToken, otp_code: otpCode } });

    const token = response.data.token;

    this.cookies.setCookie(CookiesConstants.SM_PLANNER_COOKIE_NAME, token, 1);
    this.cookies.deleteCookie(CookiesConstants.R_PLANNER_COOKIE_2FA);

    return token;
  }

  async resendOTP({
    email,
    password,
    twoFactorMethod,
  }: {
    email: string;
    password: string;
    twoFactorMethod: TwoFactorMethod;
  }): Promise<string> {
    let response;
    switch (twoFactorMethod) {
      case `email`:
        response = await this.requests.get({
          uri: `/resend-email-otp`,
          params: { email, password },
        });
        break;
      case `sms`:
        response = await this.requests.get({
          uri: `/resend-sms-otp`,
          params: { email, password },
        });
        break;
      default:
        throw new Error(`Invalid two factor method`);
    }

    const token = response.data.token;
    this.cookies.setCookie(CookiesConstants.R_PLANNER_COOKIE_2FA, token, 1);

    return token;
  }

  cleanSessionParameters() {
    this.cookies.deleteCookie(CookiesConstants.SM_PLANNER_COOKIE_NAME);
    this.cookies.deleteCookie(CookiesConstants.R_PLANNER_COOKIE_2FA);
    this.deleteAsUser();
  }

  async deleteSession(): Promise<void> {
    try {
      const token = this.cookies.getCookie(CookiesConstants.SM_PLANNER_COOKIE_NAME);

      if (!token) {
        this.cleanSessionParameters();
        throw new Error(`Could not logout: invalid session token`);
      }

      const response = await this.requests.get({
        uri: `/logout`,
        params: {},
        headers: {
          ...(token
            ? {
                Authorization: `Bearer ${token}`,
              }
            : {}),
        },
      });

      this.cleanSessionParameters();
      return response.data.token;
    } catch {
      this.cleanSessionParameters();
    }
  }

  async getCountry(): Promise<string> {
    if (this.countryCode) {
      return this.countryCode;
    }

    const requests = Requests.create(`https://get.geojs.io`);
    const response = await requests.get({ uri: `/v1/ip/geo.json` });

    if (response.status !== 200) {
      return DEFAULT_COUNTRY_CODE;
    }

    const { country_code } = response.data ?? {};
    return country_code?.toUpperCase() ?? DEFAULT_COUNTRY_CODE;
  }
}
